import { observer } from 'mobx-react-lite'
import { Navigate, useOutlet } from 'react-router'
import { useFeatures, useRootStore } from '@/contexts/root-store-context'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { useDocumentTitle } from '@/hooks/document-title'
import { ReferAFriendBanner } from '@/features/billing/referrals/ReferAFriendBanner'
import { PageNavigation, PageNavigationItem } from '@/components/PageNavigation'
import { links } from '@/utils/link'
import { Avatar } from '@/components/Avatar'
import { AccountViewCompactLayout, AccountViewSpaciousLayout } from './'

export default observer(function AccountViewContainer() {
  const isSpacious = useIsSpacious()

  return isSpacious ? <SpaciousLayoutContainer /> : <CompactLayoutContainer />
})

const CompactLayoutContainer = observer(function CompactLayoutContainer() {
  const outlet = useOutlet()
  const { iam } = useRootStore()
  const { getCurrentUser } = useFeatures((app) => ({
    getCurrentUser: app.iam.getCurrentUser,
  }))

  const currentUser = getCurrentUser()

  useDocumentTitle(outlet ? null : 'Account')

  if (outlet) {
    return outlet
  }

  return (
    <AccountViewCompactLayout
      avatar={<Avatar subject={currentUser} size={64} />}
    >
      <PageNavigationContainer />
      {!iam.session.externalCustomerEnabled && <ReferAFriendBanner />}
    </AccountViewCompactLayout>
  )
})

const SpaciousLayoutContainer = observer(function SpaciousLayoutContainer() {
  const outlet = useOutlet()
  const { iam } = useRootStore()

  if (!outlet) {
    return <Navigate replace to={links.accountPersonalInfo()} />
  }

  return (
    <AccountViewSpaciousLayout
      navigation={
        <>
          <PageNavigationContainer />
          {!iam.session.externalCustomerEnabled && <ReferAFriendBanner />}
        </>
      }
    >
      {outlet}
    </AccountViewSpaciousLayout>
  )
})

const PageNavigationContainer = observer(function PageNavigationContainer() {
  const { iam } = useRootStore()
  const isSpacious = useIsSpacious()
  const { openSupportChat, isSupportEnabled } = useFeatures(({ support }) => ({
    openSupportChat: support.openSupportChat,
    isSupportEnabled: support.isSupportEnabled,
  }))
  const showAllItems = !iam.session.externalCustomerEnabled

  return (
    <PageNavigation>
      <PageNavigationItem
        to={links.accountPersonalInfo()}
        icon="user"
        label="Personal info"
      />
      {iam.session.currentWorkspace.config.billing.enabled && (
        <PageNavigationItem
          to={links.accountBilling()}
          icon="card"
          label="Payment details"
        />
      )}
      {showAllItems && (
        <>
          <PageNavigationItem
            to={links.accountSecurity()}
            icon="shield"
            label="Security"
          />
          {isSupportEnabled() && !isSpacious && (
            <PageNavigationItem
              icon="help"
              label="Support"
              onPress={openSupportChat}
            />
          )}
          <PageNavigationItem
            icon="log-out"
            label="Log out"
            onPress={iam.logout}
          />
        </>
      )}
    </PageNavigation>
  )
})
